import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { KAFFA } from "@/constants/testIds";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLocale();
  const [form, setForm] = useState({
    razao_social: "", cnpj: "", address: "", phone: "", email: "",
    country: "", commercial_contact: "", password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await register(form);
      toast.success(t("register.success"));
      navigate("/pending");
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 mx-auto max-w-[1400px] w-full px-4 sm:px-8 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-3">{t("register.tagline")}</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-black mb-6">
            {t("auth.register_title")}
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            {t("register.intro")}
          </p>
          <div className="mt-8 border border-border bg-card p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-accent shrink-0" strokeWidth={1.5} />
              <div>
                <div className="font-display text-lg tracking-tight">{t("register.verification")}</div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("register.verification_desc")}
                </p>
              </div>
            </div>
          </div>
        </aside>

        <form onSubmit={onSubmit} className="lg:col-span-8 border border-border bg-card p-6 sm:p-10">
          {err && (
            <div className="mb-6 px-4 py-3 border-l-2 border-destructive bg-destructive/10 text-sm text-destructive">
              {err}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("auth.razao_social")}
              </Label>
              <Input
                data-testid={KAFFA.registerRazaoSocial}
                required value={form.razao_social} onChange={update("razao_social")}
                className="mt-1.5 h-11 rounded-none bg-transparent" placeholder={t("register.placeholder_razao")}
              />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("auth.cnpj")}
              </Label>
              <Input data-testid={KAFFA.registerCnpj} required value={form.cnpj} onChange={update("cnpj")}
                className="mt-1.5 h-11 rounded-none bg-transparent" placeholder={t("register.placeholder_cnpj")} />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("auth.country")}
              </Label>
              <Input data-testid={KAFFA.registerCountry} required value={form.country} onChange={update("country")}
                className="mt-1.5 h-11 rounded-none bg-transparent" placeholder={t("register.placeholder_country")} />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("auth.address")}
              </Label>
              <Textarea data-testid={KAFFA.registerAddress} required value={form.address} onChange={update("address")}
                className="mt-1.5 rounded-none bg-transparent min-h-[80px]" placeholder={t("register.placeholder_address")} />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("auth.phone")}
              </Label>
              <Input data-testid={KAFFA.registerPhone} required value={form.phone} onChange={update("phone")}
                className="mt-1.5 h-11 rounded-none bg-transparent" placeholder={t("register.placeholder_phone")} />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("auth.commercial")}
              </Label>
              <Input data-testid={KAFFA.registerCommercial} required value={form.commercial_contact} onChange={update("commercial_contact")}
                className="mt-1.5 h-11 rounded-none bg-transparent" placeholder={t("register.placeholder_contact")} />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("auth.email")}
              </Label>
              <Input data-testid={KAFFA.registerEmail} required type="email" value={form.email} onChange={update("email")}
                className="mt-1.5 h-11 rounded-none bg-transparent" placeholder={t("register.placeholder_email")} />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("auth.password")}
              </Label>
              <Input data-testid={KAFFA.registerPassword} required type="password" minLength={6}
                value={form.password} onChange={update("password")}
                className="mt-1.5 h-11 rounded-none bg-transparent" placeholder={t("register.placeholder_password")} />
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-muted-foreground">
              {t("auth.has_account")} <Link to="/login" className="text-accent hover:underline">{t("auth.signin")}</Link>
            </p>
            <Button
              type="submit"
              disabled={submitting}
              data-testid={KAFFA.registerSubmit}
              className="h-12 px-8 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-[0.2em]"
            >
              {submitting ? t("register.submitting") : t("auth.submit_register")}
              <ArrowRight className="h-3.5 w-3.5 ml-2" />
            </Button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
